import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../api';
import BrandingFileUploader from '../../components/BrandingFileUploader';
import LoginBrandingPreview from '../../components/LoginBrandingPreview';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { Buildings, CheckCircle, XCircle } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

const Field = ({ label, children }) => (
  <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>
);

const QuotaBar = ({ label, data }) => {
  const pct = data?.porcentaje;
  const color = pct == null ? 'bg-slate-300' : pct >= 100 ? 'bg-red-500' : pct >= 90 ? 'bg-orange-500' : pct >= 80 ? 'bg-amber-400' : 'bg-emerald-500';
  return (
    <div className="space-y-1" data-testid={`quota-${label}`}>
      <div className="flex justify-between text-xs">
        <span className="font-medium capitalize">{label}</span>
        <span>{data?.actual ?? 0}{data?.limite != null ? ` / ${data.limite}` : ' (sin límite)'}{data?.pendientes ? ` · ${data.pendientes} pendientes` : ''}</span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${Math.min(100, pct ?? (data?.actual ? 15 : 0))}%` }} />
      </div>
      {pct >= 80 && pct < 100 && <p className="text-[10px] text-amber-600">Te estás acercando al límite.</p>}
      {pct >= 100 && <p className="text-[10px] text-red-600 font-semibold">Límite alcanzado: nuevas altas bloqueadas.</p>}
    </div>
  );
};

export default function EstablishmentSettingsPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const institutionIdParam = searchParams.get('institution_id');
  const focusTab = searchParams.get('tab');
  const [establishments, setEstablishments] = useState([]);
  const [estId, setEstId] = useState('');
  const [s, setS] = useState(null);
  const [usage, setUsage] = useState(null);
  const [pending, setPending] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [institutions, setInstitutions] = useState([]);
  const [creatingFor, setCreatingFor] = useState(null);
  const [autoCreated, setAutoCreated] = useState(false);

  const loadEstablishments = useCallback(() => {
    setLoadingList(true);
    api.get('/establishments').then(r => {
      const items = Array.isArray(r.data) ? r.data : (r.data.establishments || []);
      setEstablishments(items);
      // If institution_id in URL, try to preselect its establishment
      if (institutionIdParam) {
        const match = items.find(e => e.institution_id === institutionIdParam);
        if (match) {
          setEstId(match.id);
        } else if (items[0]) {
          setEstId(prev => prev || items[0].id);
        }
      } else if (items[0]) {
        setEstId(prev => prev || items[0].id);
      }
      // Always load institutions (needed for empty state and auto-create by URL)
      api.get('/institutions').then(ir => {
        const insts = Array.isArray(ir.data) ? ir.data : (ir.data.institutions || []);
        // Normalize legacy name/type/etc.
        const normalized = insts
          .filter(i => i.is_active !== false)
          .map(i => ({
            ...i,
            nombre: i.nombre || i.name || '',
            ciudad: i.ciudad || i.comuna || '',
            region: i.region || '',
          }));
        setInstitutions(normalized);
      }).catch(() => {});
    }).catch((e) => {
      setLoadError(e.response?.data?.detail || 'No se pudo cargar la lista de establecimientos');
      toast.error('Error al cargar establecimientos');
    }).finally(() => setLoadingList(false));
  }, [institutionIdParam]);

  useEffect(() => { loadEstablishments(); }, [loadEstablishments]);

  // Auto-create an establishment when arriving with ?institution_id and none exists for it yet
  useEffect(() => {
    if (autoCreated) return;
    if (loadingList) return;
    if (!institutionIdParam) return;
    // If already has one for this institution, nothing to do
    const existing = establishments.find(e => e.institution_id === institutionIdParam);
    if (existing) return;
    const inst = institutions.find(i => i.id === institutionIdParam);
    if (!inst) return;
    setAutoCreated(true);
    createFromInstitution(inst);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingList, institutionIdParam, establishments, institutions, autoCreated]);

  const createFromInstitution = async (inst) => {
    setCreatingFor(inst.id);
    try {
      const r = await api.post('/establishments', {
        nombre: inst.nombre,
        institution_id: inst.id,
        ciudad: inst.ciudad || inst.comuna || null,
        region: inst.region || null
      });
      toast.success(`Establecimiento "${r.data.nombre}" creado`);
      loadEstablishments();
      setEstId(r.data.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el establecimiento');
    } finally {
      setCreatingFor(null);
    }
  };

  const load = useCallback(() => {
    if (!estId) return;
    api.get(`/establishments/${estId}/settings`).then(r => setS(r.data)).catch(() => toast.error('Error al cargar la configuración'));
    api.get(`/establishments/${estId}/usage`).then(r => setUsage(r.data)).catch(() => {});
    api.get(`/establishments/${estId}/pending-users`).then(r => setPending(r.data || [])).catch(() => {});
  }, [estId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setS(prev => ({ ...prev, [k]: v }));
  const save = async (fields) => {
    setSaving(true);
    try {
      const payload = {};
      fields.forEach(f => { payload[f] = s[f] === '' ? null : s[f]; });
      const r = await api.put(`/establishments/${estId}/settings`, payload);
      setS(r.data);
      toast.success('Configuración guardada');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al guardar'); }
    finally { setSaving(false); }
  };

  const decide = async (uid, action, role) => {
    try {
      const r = action === 'approve'
        ? await api.post(`/users/${uid}/approve`, { role })
        : await api.post(`/users/${uid}/reject`);
      toast.success(r.data.message);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const numInput = (k) => (
    <Input type="number" value={s?.[k] ?? ''} placeholder="Sin límite"
      onChange={e => set(k, e.target.value === '' ? '' : parseInt(e.target.value))} data-testid={`input-${k}`} />
  );

  return (
    <div className="space-y-5" data-testid="establishment-settings-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          {institutionIdParam && (
            <button
              onClick={() => navigate('/app/admin/establishments')}
              className="text-xs text-slate-500 hover:text-slate-800 mb-1"
              data-testid="back-to-establishments"
            >
              ← Volver a Establecimientos
            </button>
          )}
          <h1 className="text-2xl font-bold flex items-center gap-2"><Buildings size={24} className="text-cyan-600" /> Configuración del Establecimiento</h1>
          <p className="text-sm text-slate-500">Registro, dominio, branding, cupos por rol y simulaciones. Cada establecimiento (sede) tiene su propia configuración.</p>
        </div>
        <select className="border rounded-lg px-3 py-2 text-sm" value={estId} onChange={e => setEstId(e.target.value)} data-testid="establishment-selector">
          {establishments.map(e => <option key={e.id} value={e.id}>{e.nombre}</option>)}
        </select>
      </div>

      {loadingList && (
        <div className="flex items-center gap-2 text-slate-500 text-sm py-10 justify-center" data-testid="settings-loading">
          <Loader2 className="w-4 h-4 animate-spin" /> Cargando establecimientos...
        </div>
      )}

      {!loadingList && loadError && (
        <Card className="border-red-200" data-testid="settings-load-error">
          <CardContent className="py-6 text-center text-sm text-red-600">{loadError}</CardContent>
        </Card>
      )}

      {!loadingList && !loadError && establishments.length === 0 && (
        <Card data-testid="no-establishments-card">
          <CardContent className="py-10 text-center space-y-4">
            <Buildings size={40} className="mx-auto text-slate-300" />
            <div>
              <p className="font-semibold text-slate-700">No hay establecimientos registrados</p>
              <p className="text-sm text-slate-500 mt-1">
                El registro, dominio, branding y cupos se configuran por establecimiento (una sede dentro
                de una institución). Crea el establecimiento principal de cada institución con un clic:
              </p>
            </div>
            {institutions.length > 0 ? (
              <div className="max-w-lg mx-auto space-y-2 text-left">
                {institutions.map(inst => (
                  <div key={inst.id} className="flex items-center justify-between border rounded-lg px-4 py-2.5" data-testid={`institution-row-${inst.id}`}>
                    <div>
                      <p className="text-sm font-medium">{inst.nombre}</p>
                      <p className="text-xs text-slate-400">{[inst.ciudad || inst.comuna, inst.region].filter(Boolean).join(', ')}</p>
                    </div>
                    <Button size="sm" disabled={creatingFor === inst.id} onClick={() => createFromInstitution(inst)} data-testid={`create-est-for-${inst.id}`}>
                      {creatingFor === inst.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Crear establecimiento principal'}
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-400">No se encontraron instituciones activas.</p>
            )}
          </CardContent>
        </Card>
      )}

      {usage && (
        <Card data-testid="quota-dashboard">
          <CardHeader><CardTitle className="text-base">Uso del plan</CardTitle></CardHeader>
          <CardContent className="grid md:grid-cols-3 gap-5">
            <QuotaBar label="alumno" data={usage.alumno} />
            <QuotaBar label="instructor" data={usage.instructor} />
            <QuotaBar label="admin" data={usage.admin} />
          </CardContent>
        </Card>
      )}

      {pending.length > 0 && (
        <Card data-testid="pending-users-card">
          <CardHeader>
            <CardTitle className="text-base">Solicitudes pendientes ({pending.length})</CardTitle>
            <CardDescription className="text-xs">Usuarios registrados a la espera de aprobación.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {pending.map(u => (
              <div key={u.id} className="flex flex-wrap items-center gap-2 border rounded-lg p-2" data-testid={`pending-user-${u.email}`}>
                <span className="text-sm font-medium">{u.nombre} {u.apellido}</span>
                <span className="text-xs text-slate-500 flex-1">{u.email}</span>
                <select className="border rounded px-2 py-1 text-xs" defaultValue={u.role} id={`role-${u.id}`}>
                  <option value="alumno">Alumno</option><option value="instructor">Instructor</option>
                </select>
                <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => decide(u.id, 'approve', document.getElementById(`role-${u.id}`).value)}
                  data-testid={`approve-${u.email}`}><CheckCircle size={14} className="mr-1" /> Aprobar</Button>
                <Button size="sm" variant="outline" className="h-7 text-red-600"
                  onClick={() => decide(u.id, 'reject')} data-testid={`reject-${u.email}`}><XCircle size={14} className="mr-1" /> Rechazar</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {s && (
        <Card>
          <CardContent className="pt-4">
            <Tabs defaultValue={focusTab && ['general','dominio','branding','usuarios','simulaciones'].includes(focusTab) ? focusTab : 'general'}>
              <TabsList className="flex-wrap h-auto">
                {['general', 'dominio', 'branding', 'usuarios', 'simulaciones'].map(t => (
                  <TabsTrigger key={t} value={t} className="text-xs capitalize" data-testid={`settings-tab-${t}`}>{t}</TabsTrigger>
                ))}
              </TabsList>

              <TabsContent value="general" className="pt-4 space-y-3">
                <p className="text-sm text-slate-600">Establecimiento: <b>{establishments.find(e => e.id === estId)?.nombre}</b></p>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Nombre público">
                    <Input value={s.public_name || ''} onChange={e => set('public_name', e.target.value)} data-testid="input-public-name" />
                  </Field>
                  <Field label="Mensaje de bienvenida">
                    <Input value={s.welcome_message || ''} onChange={e => set('welcome_message', e.target.value)} />
                  </Field>
                </div>
                <Button size="sm" onClick={() => save(['public_name', 'welcome_message'])} disabled={saving} data-testid="save-general-btn">
                  {saving && <Loader2 className="w-3 h-3 mr-1 animate-spin" />} Guardar
                </Button>
              </TabsContent>

              <TabsContent value="dominio" className="pt-4 space-y-3">
                <Field label="Subdominio SimuClinic">
                  <div className="flex items-center gap-2 max-w-md">
                    <Input value={s.subdomain || ''} onChange={e => set('subdomain', e.target.value.toLowerCase())} placeholder="ucm" data-testid="input-subdomain" />
                    <span className="text-sm text-slate-500 whitespace-nowrap">.simuclinic.cl</span>
                  </div>
                </Field>
                {s.subdomain && <p className="text-xs text-cyan-700">Vista previa: <b>https://{s.subdomain}.simuclinic.cl</b></p>}
                <p className="text-[11px] text-amber-600">⚠ Requiere Cloudflare Worker configurado con `X-SimuClinic-Proxy-Secret`. La detección tenant-aware en el backend ya está implementada.</p>
                <div className="flex flex-wrap items-center gap-2">
                  <Button size="sm" onClick={() => save(['subdomain'])} disabled={saving} data-testid="save-domain-btn">Guardar</Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={async () => {
                      setSaving(true);
                      try {
                        const r = await api.post(`/establishments/${estId}/verify-subdomain`);
                        const d = r.data;
                        if (d.status === 'active') {
                          toast.success(`Subdominio verificado (${d.source})`);
                        } else if (d.status === 'mismatch') {
                          toast.error('El subdominio responde pero apunta a otro establecimiento');
                        } else {
                          toast.error(`No se pudo verificar: ${(d.error || '').slice(0, 100)}`);
                        }
                      } catch (e) {
                        toast.error(e.response?.data?.detail || 'Error de verificación');
                      } finally {
                        setSaving(false);
                      }
                    }}
                    disabled={saving || !s.subdomain}
                    data-testid="verify-subdomain-btn"
                  >
                    Verificar subdominio
                  </Button>
                </div>
              </TabsContent>

              <TabsContent value="branding" className="pt-4 space-y-4">
                <div className="grid lg:grid-cols-[1fr,400px] gap-6">
                  <div className="space-y-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      {[['primary_color', 'Color principal'], ['secondary_color', 'Color secundario'], ['accent_color', 'Color de acento']].map(([k, l]) => (
                        <Field key={k} label={l}>
                          <div className="flex gap-2">
                            <input type="color" value={s[k] || '#0891b2'} onChange={e => set(k, e.target.value)} className="h-9 w-12 border rounded" data-testid={`input-${k}`} />
                            <Input value={s[k] || ''} onChange={e => set(k, e.target.value)} placeholder="#0891b2" />
                          </div>
                        </Field>
                      ))}
                    </div>
                    <div className="grid md:grid-cols-3 gap-4 pt-1">
                      <BrandingFileUploader
                        establishmentId={estId}
                        kind="logo"
                        label="Logo institucional"
                        accept="image/png,image/jpeg,image/webp,image/svg+xml"
                        maxKB={3072}
                        currentUrl={s.logo_url}
                        onChange={(url) => { setS(prev => ({ ...prev, logo_url: url })); }}
                        hint="PNG, JPG, WEBP o SVG (máx 3 MB)"
                      />
                      <BrandingFileUploader
                        establishmentId={estId}
                        kind="favicon"
                        label="Favicon"
                        accept="image/png,image/jpeg,image/webp,image/x-icon"
                        maxKB={512}
                        currentUrl={s.favicon_url}
                        onChange={(url) => { setS(prev => ({ ...prev, favicon_url: url })); }}
                        hint="PNG o ICO (máx 512 KB)"
                      />
                      <BrandingFileUploader
                        establishmentId={estId}
                        kind="background"
                        label="Imagen de fondo del login"
                        accept="image/png,image/jpeg,image/webp"
                        maxKB={8192}
                        currentUrl={s.login_background_url}
                        onChange={(url) => { setS(prev => ({ ...prev, login_background_url: url })); }}
                        hint="PNG, JPG o WEBP (máx 8 MB)"
                      />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                      <input type="checkbox" checked={!!s.branding_enabled} onChange={e => set('branding_enabled', e.target.checked)} data-testid="branding-enabled-check" />
                      Activar branding institucional (se aplicará en login, plataforma y certificados PDF)
                    </label>
                    <Button size="sm" onClick={() => save(['branding_enabled', 'primary_color', 'secondary_color', 'accent_color'])} disabled={saving} data-testid="save-branding-btn">Guardar</Button>
                  </div>

                  {/* Live login preview */}
                  <div>
                    <LoginBrandingPreview
                      institutionName={establishments.find(e => e.id === estId)?.nombre}
                      primaryColor={s.primary_color}
                      secondaryColor={s.secondary_color}
                      accentColor={s.accent_color}
                      logoUrl={s.logo_url}
                      backgroundUrl={s.login_background_url}
                      poweredByEnabled
                    />
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="usuarios" className="pt-4 space-y-3">
                <div className="grid md:grid-cols-3 gap-4">
                  <Field label="Máximo alumnos">{numInput('max_students')}</Field>
                  <Field label="Máximo instructores">{numInput('max_instructors')}</Field>
                  <Field label="Máximo administradores">{numInput('max_admins')}</Field>
                  <Field label="Registro público">
                    <select className="w-full border rounded-lg px-2 py-2 text-sm" value={s.registration_enabled ? '1' : '0'}
                      onChange={e => set('registration_enabled', e.target.value === '1')} data-testid="registration-enabled-select">
                      <option value="1">Habilitado</option><option value="0">Deshabilitado</option>
                    </select>
                  </Field>
                  <Field label="Aprobación de nuevos usuarios">
                    <select className="w-full border rounded-lg px-2 py-2 text-sm" value={s.registration_approval_mode}
                      onChange={e => set('registration_approval_mode', e.target.value)} data-testid="approval-mode-select">
                      <option value="automatica">Automática</option>
                      <option value="aprobacion">Requiere aprobación del administrador</option>
                      <option value="cerrado">Registro cerrado</option>
                    </select>
                  </Field>
                  <Field label="Solicitudes de instructor">
                    <select className="w-full border rounded-lg px-2 py-2 text-sm" value={s.allow_instructor_requests ? '1' : '0'}
                      onChange={e => set('allow_instructor_requests', e.target.value === '1')}>
                      <option value="0">No permitir</option><option value="1">Permitir</option>
                    </select>
                  </Field>
                </div>
                <Button size="sm" onClick={() => save(['max_students', 'max_instructors', 'max_admins', 'registration_enabled', 'registration_approval_mode', 'allow_instructor_requests'])} disabled={saving} data-testid="save-users-btn">Guardar</Button>
              </TabsContent>

              <TabsContent value="simulaciones" className="pt-4 space-y-3">
                <div className="grid md:grid-cols-2 gap-4 max-w-xl">
                  <Field label="Límite de simulaciones por alumno">{numInput('simulations_per_student')}</Field>
                  <Field label="Periodicidad">
                    <select className="w-full border rounded-lg px-2 py-2 text-sm" value={s.simulation_limit_period || 'sin_limite'}
                      onChange={e => set('simulation_limit_period', e.target.value)} data-testid="sim-period-select">
                      <option value="sin_limite">Sin límite</option><option value="mensual">Mensual</option>
                      <option value="trimestral">Trimestral</option><option value="semestral">Semestral</option>
                      <option value="anual">Anual</option><option value="total">Total contratado</option>
                    </select>
                  </Field>
                </div>
                <p className="text-[11px] text-amber-600">⚠ La contabilización y bloqueo de consumo se activará en la Fase C (límites de simulaciones + timeout de servidor).</p>
                <Button size="sm" onClick={() => save(['simulations_per_student', 'simulation_limit_period'])} disabled={saving} data-testid="save-sims-btn">Guardar</Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
